import * as React from "react";
import { ensureGoogleLibrary } from "./googleMapsLoader";
import { useDebouncedValue } from "../../../utils/useDebouncedValue";
import { formatFullAddress, type AddressParts, type GeoCoords } from "../../../utils/geoLinks";

/**
 * Geokodiert eine Objektanschrift über die Google-Maps-JS-API (Library
 * `geocoding`) — die Koordinaten braucht die 3D-Schrägansicht in Google Earth.
 *
 * Bewusste Entscheidungen:
 * - **Kein Geokodieren im `onClick`.** Ein `window.open` nach einem `await`
 *   gilt Safari/Firefox als Popup und wird blockiert. Die Koordinaten werden
 *   deshalb vorab aufgelöst, damit beide Buttons echte `<a href>` bleiben
 *   (Mittelklick, „In neuem Tab öffnen", Touch).
 * - **Präzisions-Gate.** Nur `ROOFTOP` und `RANGE_INTERPOLATED` treffen das
 *   Gebäude. `GEOMETRIC_CENTER`/`APPROXIMATE` sind Straßen- bzw.
 *   Orts-Zentroide — die Kamera stünde geneigt vor dem falschen Haus, was
 *   schlimmer ist als die bisherige Adresssuche. Ebenso abgelehnt:
 *   `partial_match` (Google hat die Adresse „zurechtgebogen").
 * - **Datenschutz/Kosten.** Die Adresse geht schon beim Tippen an Google,
 *   deshalb Debounce (700 ms), ein Gate auf halbwegs vollständige Adressen und
 *   ein modulweiter Cache, der jede Adresse höchstens einmal abfragt.
 */

/** Wartezeit nach der letzten Eingabe, bevor die Adresse an Google geht. */
export const GEOCODE_DEBOUNCE_MS = 700;

/** Genauigkeitsstufen, die das einzelne Gebäude treffen. */
const AKZEPTIERTE_LOCATION_TYPES = new Set(["ROOFTOP", "RANGE_INTERPOLATED"]);

/** Nur Treffer landen im Cache — Fehlschläge nie, damit ein Netzfehler nicht
 *  dauerhaft „keine Koordinaten" zementiert. */
const cache = new Map<string, GeoCoords>();

/** Laufende Anfragen je Adresse. Ohne diese Deduplizierung feuern
 *  StrictMode-Doppelmounts und zwei gleichzeitig sichtbare Anschrift-Karten
 *  dieselbe (kostenpflichtige) Anfrage mehrfach. */
const inFlight = new Map<string, Promise<GeoCoords | null>>();

/** Nur für Tests: Cache und laufende Anfragen zurücksetzen. */
export function _resetGeocodeCacheFuerTests(): void {
  cache.clear();
  inFlight.clear();
}

const cacheKey = (address: string): string => address.trim().toLowerCase().replace(/\s+/g, " ");

/**
 * Synchroner Blick in den Cache — wird beim Rendern genutzt, damit ein Klick
 * direkt nach dem Wiederbetreten des Schritts nicht noch die 2D-URL erwischt.
 */
export function getCachedCoords(address: string): GeoCoords | null {
  const key = cacheKey(address);
  if (!key) return null;
  return cache.get(key) ?? null;
}

/**
 * Liest lat/lng aus dem, was der Geocoder in `geometry.location` liefert.
 *
 * FALLE: `location` ist im Normalfall ein `google.maps.LatLng`, dessen
 * `lat()`/`lng()` **Prototyp-Methoden** sind — sie brauchen ihren Receiver.
 * Eine Methode erst herauszulösen (`const f = loc.lat; f()`) liefert entweder
 * `undefined` oder wirft im strict mode; die Geokodierung wäre damit ein
 * stiller No-op. Deshalb wird hier immer AUF dem Objekt aufgerufen. `toJSON()`
 * gibt es zusätzlich, und Plain-Objekte (`{ lat: 50.9, lng: 6.9 }`) kommen aus
 * Tests und aus manchen API-Antworten — beides wird mitgenommen.
 */
export function leseKoordinaten(location: unknown): GeoCoords | null {
  if (!location || (typeof location !== "object" && typeof location !== "function")) return null;
  const obj = location as Record<string, unknown>;

  const alsZahl = (wert: unknown): number | null => {
    const zahl = Number(wert);
    return Number.isFinite(zahl) ? zahl : null;
  };

  const feld = (name: "lat" | "lng"): number | null => {
    const wert = obj[name];
    // Methode: MIT Receiver aufrufen, sonst fehlt `this`.
    if (typeof wert === "function") {
      try {
        return alsZahl((wert as (this: unknown) => unknown).call(obj));
      } catch {
        return null;
      }
    }
    return alsZahl(wert);
  };

  let lat = feld("lat");
  let lng = feld("lng");

  if ((lat === null || lng === null) && typeof obj.toJSON === "function") {
    try {
      const json = (obj.toJSON as (this: unknown) => unknown).call(obj) as
        | Record<string, unknown>
        | null;
      if (json) {
        lat = lat ?? alsZahl(json.lat);
        lng = lng ?? alsZahl(json.lng);
      }
    } catch {
      /* egal — unten fällt es auf null zurück */
    }
  }

  if (lat === null || lng === null) return null;
  return { lat, lng };
}

/**
 * Geokodiert eine Adresse. Liefert `null`, wenn kein Key gesetzt ist, die
 * Geocoding API im Google-Cloud-Projekt fehlt, der Treffer unpräzise ist oder
 * irgendetwas schiefgeht — die Aufrufer fallen dann auf die bisherigen
 * Such-Links zurück. Wirft nie.
 */
export function geocodeAddress(address: string): Promise<GeoCoords | null> {
  const key = cacheKey(address);
  if (!key) return Promise.resolve(null);
  const treffer = cache.get(key);
  if (treffer) return Promise.resolve(treffer);
  // Läuft dieselbe Adresse schon? Dann dranhängen statt ein zweites Mal fragen
  // (StrictMode-Doppelmount, zwei gleichzeitig sichtbare Karten, …).
  const laufend = inFlight.get(key);
  if (laufend) return laufend;

  const libPromise = ensureGoogleLibrary("geocoding");
  if (!libPromise) return Promise.resolve(null);

  const anfrage = (async (): Promise<GeoCoords | null> => {
    try {
      const lib = await libPromise;
      if (!lib || typeof lib.Geocoder !== "function") return null;
      const geocoder = new lib.Geocoder();
      const antwort = await geocoder.geocode({ address, region: "de" });
      const ergebnisse = Array.isArray(antwort?.results) ? antwort.results : [];
      const bester = ergebnisse[0];
      if (!bester) return null;
      // Präzisions-Gate — siehe Kopfkommentar.
      if (bester.partial_match === true) return null;
      const geometry = bester.geometry;
      if (!geometry || !AKZEPTIERTE_LOCATION_TYPES.has(String(geometry.location_type))) return null;
      const coords = leseKoordinaten(geometry.location);
      if (!coords) return null;
      cache.set(key, coords);
      return coords;
    } catch (err) {
      console.warn("[GMaps] Geokodierung fehlgeschlagen — Karten-Links bleiben bei der Adresssuche:", err);
      return null;
    } finally {
      inFlight.delete(key);
    }
  })();

  inFlight.set(key, anfrage);
  return anfrage;
}

/**
 * Gate gegen halbe Adressen: ohne Straße (und ohne PLZ oder Ort) liefert
 * Google bestenfalls einen Orts-Zentroiden — der wird ohnehin verworfen, also
 * wird gar nicht erst gefragt.
 */
export function istGeokodierbar(parts: AddressParts): boolean {
  const street = (parts.street ?? "").trim();
  const postalCode = (parts.postalCode ?? "").trim();
  const city = (parts.city ?? "").trim();
  return street.length > 0 && (postalCode.length > 0 || city.length > 0);
}

/**
 * Liefert die Koordinaten zur aktuellen Adresse — oder `null`, solange sie
 * (noch) nicht präzise aufgelöst ist. `enabled=false` schaltet die
 * Geokodierung komplett ab (z. B. für ein zugeklapptes `<details>`, damit beim
 * Rendern nicht 1+N Adressen an Google gehen).
 */
export function useGeocodedAddress(parts: AddressParts, enabled = false): GeoCoords | null {
  const address = formatFullAddress(parts);
  const aktiv = enabled && istGeokodierbar(parts);
  const debouncedAddress = useDebouncedValue(aktiv ? address : "", GEOCODE_DEBOUNCE_MS);

  // Der Cache ist die Quelle der Wahrheit; `tick` erzwingt nur ein Re-Render,
  // sobald eine laufende Abfrage etwas eingetragen hat.
  const [, setTick] = React.useState(0);

  React.useEffect(() => {
    if (!debouncedAddress) return;
    // Schon im Cache → der Render hat die Koordinaten bereits.
    if (getCachedCoords(debouncedAddress)) return;
    let abgebrochen = false;
    void geocodeAddress(debouncedAddress).then((coords) => {
      // Abbruch-Flag: StrictMode mountet Effekte doppelt, und der Schritt kann
      // während der Abfrage verlassen werden.
      if (!abgebrochen && coords) setTick((t) => t + 1);
    });
    return () => {
      abgebrochen = true;
    };
  }, [debouncedAddress]);

  // Beim Tippen ändert sich `address` sofort, `debouncedAddress` erst später —
  // solange die neue Adresse nicht aufgelöst ist, gibt es hier bewusst `null`
  // (statt der Koordinaten der vorherigen Adresse).
  return aktiv ? getCachedCoords(address) : null;
}
